import { useState } from "react";
import AnMultipleSuggestionInputBox from "../../../input_utils/AnMultipleSuggestionBox";
import { Popup } from "../../../../design_system/components/Popup/Popup";
import PostPreview from "./PostPreview";
import { InputBox } from "../../../../design_system/components/Input Fields/InputBox";
import { InputArea } from "../../../../design_system/components/Input Fields/InputArea";
import ConditionalRender from "../../../../ConditionalRender";
import { SingleDateFilterDropdown } from "../../../admin_views/teams/singleDateFilter";
import { dateFormator, datepicker_input_format } from "../../../utils/dateUtils";
import { apiAgent } from "../../../../axiosConfig";
import { isTeamContextHandled } from "../../../../teamContext/teamSwitchService";
import { useToast } from "../../../../design_system/components/Toast/ToastContext";


const DEFAULT_LABELS = { popupName: "Publish SEC filing", submitBtn: "Publish Article" }

const defaultArticle = { description: "", url: "", image_url: "", title: "", published_at: "", prospects: [] };
const initialArticleState = (aggregateData) => ({
    ...defaultArticle,
    ...aggregateData?.sharable_article,
    prospects: aggregateData?.prospects || [],
});

const initialPopupState = { isLoading: false, errors: {}, showDateDropdown: false, showPreview: false };

const SecPublishPopup = ({
    onCloseCallback,
    labels = {},
    aggregateData = {},
    onPublishCallback,
    publish_url,
    ...props
}) => {
    const popupLabels = { ...DEFAULT_LABELS, ...labels }
    const [article, setArticle] = useState(() => initialArticleState(aggregateData));
    const [popupState, setPopupState] = useState(initialPopupState);
    const updatePopupState = (payload) => setPopupState(prev => ({ ...prev, ...payload }));
    const toast = useToast();

    const setArticleField = (key, value) => {
        setArticle(prev => ({ ...prev, [key]: value }));
        updatePopupState({ errors: {} });
    };

    const handlePostPublish = () => {
        const tagged_prospect_ids = article.prospects.map((p) => p.id);
        let errors = {};
        if (tagged_prospect_ids.length === 0) errors["publish_error_message"] = "It is mandatory to tag prospects to create post";
        if (!article.url) errors["url"] = "Article url can't be empty";
        if (Object.keys(errors).length > 0) {
            updatePopupState({ errors });
            return;
        }
        updatePopupState({ isLoading: true, errors: {} });
        apiAgent({
            method: "post",
            url: publish_url,
            data: {
                tagged_prospect_ids,
                published_at: datepicker_input_format(new Date(article.published_at)),
                article_title: article.title,
                check_pub_guideline_ack: !!props.checkGuidelineAcknowledgement,
            },
        }).then(res => {
            onCloseCallback()
            updatePopupState({ isLoading: false, errors: {} })
            onPublishCallback?.(aggregateData, res.data)
        }).catch(err => {
            if (isTeamContextHandled(err)) {
                updatePopupState({ isLoading: false, errors: {} });
                return;
            }
            if (err.response?.data?.error === 'ack_failed') props.renderGuidelinesUpdateWarning(err.response.data.data);
            updatePopupState({ isLoading: false, errors: { submitError: "Post was not created successfully" } })
        })
    }

    return (
        <Popup
            name={popupLabels.popupName}
            handleOnClosePopup={onCloseCallback}
            maxWidth={600}
            customContentStyle={{ padding: "0px" }}
            customPopupStyle={{ maxHeight: "98vh" }}
            primaryAction={{
                label: popupLabels.submitBtn,
                onClick: handlePostPublish,
                isLoading: popupState.isLoading,
                error: popupState.errors?.publish_error_message,
                helperText: popupState.errors?.publish_error_message,
                showHelperText: true,
            }}
            secondaryAction={{
                label: "Cancel",
                onClick: onCloseCallback,
            }}
        >
            <div className="publish-popup__body" style={{ paddingTop: "var(--space-m)", paddingBottom: "var(--space-m)" }}>
                <div className="publish-popup__section" style={{ paddingRight: 0 }}>
                    <AnMultipleSuggestionInputBox
                        label={<span className="body-s-semibold color-text-secondary">Add prospects related to news article</span>}
                        inputClass="an_search_box"
                        placeholder="Search Prospect"
                        suggestionUrl="/prospects/search?team_search=true"
                        selectedOptions={article.prospects}
                        callback={(options) => setArticle(prev => ({ ...prev, prospects: [...options] }))}
                        theme="tagged_options"
                    />
                </div>

                <InputBox
                    label="Article Link"
                    showLabel
                    Size="Large"
                    error={popupState.errors?.url}
                    helperText={popupState.errors?.url}
                    showHelperText={!!popupState.errors?.url}
                    disabled
                    type="text"
                    value={article.url}
                    placeholder="Article url"
                />
                <div className="publish-popup__copy-link">
                    <div className="link-default support-m cursor-pointer publish-popup__copy-link-text"
                        onClick={() => { navigator.clipboard.writeText(article.url); toast.success("Article link copied", { duration: 1500 }) }}>
                        Copy Link
                    </div>
                </div>

                <div className="flex items-start gap-m publish-popup__date-row">
                    <div className="publish-popup__date-wrapper">
                        <InputBox
                            label="Article Published Date"
                            showLabel
                            Size="Large"
                            type="text"
                            value={dateFormator(new Date(article.published_at), { type: "input-format" }, "-")}
                            onClick={() => updatePopupState({ showDateDropdown: true })}
                            placeholder="dd/mm/yyyy"
                            readOnly
                            innerInputStyle={{ cursor: 'pointer' }}
                        />
                        <ConditionalRender if={popupState.showDateDropdown}>
                            <SingleDateFilterDropdown
                                toggleDropDown={() => updatePopupState({ showDateDropdown: !popupState.showDateDropdown })}
                                currDate={new Date(article.published_at || new Date())}
                                setCurrDate={(date) => {
                                    setArticleField("published_at", dateFormator(new Date(date), { type: "input-format" }, "-"));
                                    updatePopupState({ showDateDropdown: false });
                                }}
                            />
                        </ConditionalRender>
                    </div>
                </div>

                <InputArea
                    label="Article Title"
                    showLabel
                    Size="Medium"
                    customStyle={{ marginTop: "var(--space-m)" }}
                    value={article.title || ""}
                    handleOnChange={(e) => setArticleField("title", e.target.value)}
                />

            </div>

            <PostPreview
                article={article}
                isOpen={popupState.showPreview}
                onToggle={() => updatePopupState({ showPreview: !popupState.showPreview })}
            />
        </Popup>
    )
}

export default SecPublishPopup;
